import { Link, useLocation } from '@tanstack/react-router'
import {
  LayoutDashboard,
  Search,
  Radio,
  User,
  Users,
  ExternalLink,
  Settings,
  Database,
  Languages,
  MessageSquare,
  Tags,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { classNames } from '@/ui/utils/main'
import { useAuthStore } from '@/application/stores/auth.store'
import { useSidebarStore } from '@/application/stores/sidebar.store'
import { useGetSiteIdentity } from '@/application/services/settings.service'
import { useCurrentTheme } from '@/application/hooks/useCurrentTheme'

export function Sidebar() {
  const location = useLocation()
  const { user } = useAuthStore()
  const { t, i18n } = useTranslation()
  const { isOpen, close } = useSidebarStore()
  const sidebarLang = i18n.language.substring(0, 2).toLowerCase() || 'fr'
  const { data: settings } = useGetSiteIdentity(sidebarLang)
  const currentTheme = useCurrentTheme()
  const effectiveName = settings?.siteName || t('app_name')

  const isPrivileged =
    user?.role === 'streamer' || user?.role === 'moderator' || user?.role === 'admin'
  // Live section is only for streamers (Twitch-facing). Admins are web admins and don't stream.
  // Also requires Twitch to be enabled via the VITE_TWITCH_ENABLE feature flag.
  const twitchEnabled = import.meta.env.VITE_TWITCH_ENABLE === 'true'
  const canAccessLive = (user?.role === 'streamer' || user?.role === 'moderator') && twitchEnabled

  const navItems = [
    {
      label: t('admin.nav_dashboard'),
      href: '/manage/dashboard',
      icon: LayoutDashboard,
      show: isPrivileged,
    },
    {
      label: t('admin.nav_profile'),
      href: '/manage/profile',
      icon: User,
      show: true,
    },
    {
      label: t('admin.nav_live'),
      href: '/manage/live',
      icon: Radio,
      show: canAccessLive,
    },
    {
      label: t('admin.nav_categories'),
      href: '/manage/categories',
      icon: Tags,
      show: user?.role === 'admin' || user?.role === 'streamer',
    },
    {
      label: t('admin.nav_investigations'),
      href: '/manage/investigations',
      icon: Search,
      show: true,
    },
    {
      label: t('admin.nav_comments'),
      href: '/manage/comments',
      icon: MessageSquare,
      show: isPrivileged,
    },
    {
      label: t('admin.nav_investigators'),
      href: '/manage/investigators',
      icon: Users,
      show: user?.role === 'admin' || user?.role === 'streamer',
    },
    {
      label: t('admin.nav_import_export'),
      href: '/manage/import-export',
      icon: Database,
      show: user?.role === 'admin' || user?.role === 'streamer',
    },
    {
      label: t('admin.nav_translations'),
      href: '/manage/translations',
      icon: Languages,
      show: user?.role === 'admin' || user?.role === 'streamer',
    },
    {
      label: t('admin.nav_settings'),
      href: '/manage/settings',
      icon: Settings,
      show: user?.role === 'admin' || user?.role === 'streamer',
    },
  ]

  return (
    <>
      {/*
        Mobile backdrop — closes sidebar on tap outside.
        `z-40` sits between the admin header (`z-30`) and the drawer (`z-50`),
        so the backdrop dims the header along with the page while the drawer
        itself stays fully lit above both. See the comment in Header.tsx.
      */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={close}
          aria-hidden="true"
        />
      )}

      <aside
        className={classNames(
          // Size & appearance.
          // `h-screen-safe` rather than `h-screen`: the body pads the bottom
          // safe-area inset in a standalone PWA window (common.css), which
          // shrinks the body without shrinking the viewport — a `100dvh` child
          // then overflows by exactly the home-indicator height, which on the
          // `lg:sticky` desktop path pushes the "go to site" footer off-screen.
          // `h-screen-safe` subtracts that inset back out (components.css).
          'w-64 shrink-0 bg-background border-r border-border h-screen-safe flex flex-col',
          // `pt-safe-top` for the same reason the header needs it: while
          // `fixed` this element is positioned against the viewport rather than
          // the body, and the body deliberately never pads the TOP inset
          // (common.css), so without this the logo block runs under the status
          // bar in portrait.
          // There is deliberately no horizontal counterpart: the app does not
          // inset the left/right cutouts anywhere (a product decision recorded
          // in common.css — full-bleed width in landscape beats clearing the
          // notch), so the drawer runs edge to edge in landscape like every
          // other surface, and `w-64` above stays a plain 16rem.
          'pt-safe-top',
          // Positioning: fixed overlay on mobile, sticky in-flow on desktop.
          // `z-50` keeps the open drawer above the admin header (`z-30`) and
          // its own backdrop (`z-40`).
          'fixed top-0 left-0 z-50 lg:sticky',
          // Slide animation
          'transition-transform duration-300 ease-in-out',
          // Visibility: hidden on mobile unless open; always visible on desktop
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img
              src={currentTheme.logoUrl}
              alt=""
              aria-hidden="true"
              className="h-10 w-10 object-contain"
            />
            <div>
              <h1 className="font-bold text-lg">{effectiveName}</h1>
              <p className="text-sm text-muted-foreground">{t('admin.sidebar_admin')}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {navItems
              .filter((item) => item.show && !!user)
              .map((item) => {
                const isActive = location.pathname.startsWith(item.href)
                const Icon = item.icon
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      onClick={close}
                      className={classNames(
                        'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                )
              })}
          </ul>
        </nav>

        {/* Bottom section */}
        <div className="p-4 border-t border-border">
          {/* Go to site */}
          <a
            href="/"
            onClick={close}
            className="flex items-center gap-3 px-4 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-lg transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            <span>Aller sur le site</span>
          </a>
        </div>
      </aside>
    </>
  )
}
